import {
  Alert,
  AppShellContent,
  Button,
  EmptyState,
  Group,
  Headline,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@assetwatch/ui";
import { WorkRole } from "@assetwatch/work-authorization";

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AssetWorkOrderModal } from "../components/AssetWorkOrderModal";
import { WorkPage, WorkPageState } from "../components/layout/WorkPageLayout";
import {
  type ListFacet,
  ListFilters,
} from "../components/snowflakes/ListFilters";
import { SearchFilterToolbar } from "../components/snowflakes/SearchFilterToolbar";
import { useWorkScope } from "../components/WorkScopeProvider";
import { WorkOrderSplitView } from "../components/work-orders/WorkOrderSplitView";
import { canPerformAction } from "../lib/access";
import type { WorkOrderId } from "../lib/workIds";
import {
  buildWorkOrderDetailSearch,
  type WorkOrdersFilterSearch,
  type WorkOrdersSearch,
} from "../lib/workOrdersSearch";
import { getWorkScopeSearch } from "../lib/workSearch";
import { useAssetWorkOrderCreation } from "../queries/useAssetWorkOrderCreation";
import { useWorkOrdersQuery } from "../queries/useWorkOrdersQuery";

type FilterKey =
  | "priorities"
  | "statuses"
  | "workTypes"
  | "assetIds"
  | "categoryIds"
  | "userAssignments"
  | "teamAssignments";
function toIsoDate(date: Date | null) {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function lookupOptions(items: { code: string; name: string }[] | undefined) {
  return (items ?? []).map(({ code, name }) => ({ value: code, label: name }));
}
// The same four tabs apply to every role; product defined this list and it
// intentionally differs from the prototype's All / My work / Done tabs.
type WorkOrderTab = NonNullable<WorkOrdersSearch["workOrderView"]>;
const tabs: {
  value: WorkOrderTab;
  label: string;
  countKey: "mine" | "created" | "open" | "done";
}[] = [
  { value: "mine", label: "Assigned to me", countKey: "mine" },
  { value: "created", label: "Created by me", countKey: "created" },
  { value: "open", label: "All Open", countKey: "open" },
  { value: "done", label: "Done", countKey: "done" },
];
function dateFromSearch(value?: string) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function WorkOrdersScreen({
  linkedWorkOrderId,
  routeSearch,
}: {
  linkedWorkOrderId?: WorkOrderId;
  routeSearch?: WorkOrdersSearch;
}) {
  const [routeState, setRouteState] = useState<WorkOrdersSearch>(
    routeSearch ?? {},
  );
  useEffect(() => setRouteState(routeSearch ?? {}), [routeSearch]);
  const navigate = useNavigate({ from: "/work-orders" });
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const { createWorkOrder, attachProcedure } = useAssetWorkOrderCreation();
  const view = routeState.workOrderView ?? "mine";
  const search = routeState.workOrderSearch ?? "";
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const page = routeState.workOrderPage ?? 1;
  const filters: Record<FilterKey, string[]> = {
    priorities: routeState.workOrderPriorities ?? [],
    statuses: routeState.workOrderStatuses ?? [],
    workTypes: routeState.workOrderTypes ?? [],
    assetIds: routeState.workOrderAssetIds ?? [],
    categoryIds: routeState.workOrderCategoryIds ?? [],
    userAssignments: (routeState.workOrderAssignees ?? []).filter((code) =>
      code.startsWith("user-"),
    ),
    teamAssignments: (routeState.workOrderAssignees ?? []).filter((code) =>
      code.startsWith("team-"),
    ),
  };
  const dueDateFrom = dateFromSearch(routeState.workOrderDueDateFrom);
  const dueDateTo = dateFromSearch(routeState.workOrderDueDateTo);
  const [isNewWorkOrderOpen, setIsNewWorkOrderOpen] = useState(false);
  const [prioritySortDirection, setPrioritySortDirection] = useState<
    "asc" | "desc"
  >("asc");
  const selectedWorkOrderId =
    linkedWorkOrderId ?? routeState.workOrderSelectedId ?? null;
  function updateRouteSearch(update: Partial<WorkOrdersSearch>) {
    setRouteState((previous) => ({ ...previous, ...update }));
    void navigate({
      replace: true,
      ...(linkedWorkOrderId
        ? {
            to: "/work-orders/$workOrderId" as const,
            params: { workOrderId: linkedWorkOrderId },
          }
        : { to: "/work-orders" as const }),
      search: (previous) => ({ ...previous, ...update }),
    });
  }
  function updateFilters(next: Record<FilterKey, string[]>) {
    const searchUpdate: WorkOrdersFilterSearch = {
      workOrderPriorities: next.priorities,
      workOrderStatuses: next.statuses,
      workOrderTypes: next.workTypes,
      workOrderAssetIds: next.assetIds,
      workOrderCategoryIds: next.categoryIds,
      workOrderAssignees: [...next.userAssignments, ...next.teamAssignments],
    };
    updateRouteSearch({ ...searchUpdate, workOrderPage: 1 });
  }
  function clearFilters() {
    setDebouncedSearch("");
    updateRouteSearch({
      workOrderView: "mine",
      workOrderSearch: "",
      workOrderPage: 1,
      workOrderPriorities: [],
      workOrderStatuses: [],
      workOrderTypes: [],
      workOrderAssetIds: [],
      workOrderCategoryIds: [],
      workOrderAssignees: [],
      workOrderDueDateFrom: undefined,
      workOrderDueDateTo: undefined,
    });
  }
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const query = useWorkOrdersQuery({
    view,
    page,
    search: debouncedSearch,
    priorities: filters.priorities,
    statuses: filters.statuses,
    workTypes: filters.workTypes,
    assetIds: filters.assetIds,
    categoryIds: filters.categoryIds,
    assignees: [...filters.userAssignments, ...filters.teamAssignments],
    dueDateFrom: toIsoDate(dueDateFrom),
    dueDateTo: toIsoDate(dueDateTo),
    // PriorityOrder is ascending by urgency (Highest has the lowest value).
    direction: prioritySortDirection,
    sort: "priority",
  });
  const desiredPageCount = page;
  useEffect(() => {
    if (
      (query.data?.pageCount ?? 1) < desiredPageCount &&
      query.hasNextPage &&
      !query.isFetching &&
      !query.isFetchNextPageError
    ) {
      void query.fetchNextPage();
    }
  }, [
    desiredPageCount,
    query.data?.pageCount,
    query.fetchNextPage,
    query.hasNextPage,
    query.isFetchNextPageError,
    query.isFetching,
  ]);
  const activeFilterCount =
    Object.values(filters).reduce(
      (count, selected) => count + selected.length,
      0,
    ) +
    (dueDateFrom ? 1 : 0) +
    (dueDateTo ? 1 : 0);
  const hasActiveFilters =
    activeFilterCount > 0 || search.trim().length > 0 || view !== "mine";
  const canCreateWorkOrder = selectedFacility
    ? canPerformAction(selectedFacility, "CREATE_WORK_ORDER")
    : false;
  const canMarkWorkOrdersDone = selectedFacility
    ? canPerformAction(selectedFacility, "COMPLETE_WORK_ORDER")
    : false;
  const canAssignWorkOrdersToMe = selectedFacility
    ? canPerformAction(selectedFacility, "CLAIM_WORK_ORDER")
    : false;
  const canDeleteWorkOrders = selectedFacility?.role === WorkRole.ADMINISTRATOR;
  const data = query.data;
  const metadata = data?.metadata;
  const assigneeOptions = metadata?.assignees ?? [];
  /* @ds-deviation: the POC Work Orders panel (workorders.jsx) lists a different
     facet set; this is the product-approved set — fix in POC */
  const workOrderFacets: ListFacet[] = (
    [
      ["statuses", "Status", metadata?.statuses],
      ["priorities", "Priority", metadata?.priorities],
      ["workTypes", "Work Type", metadata?.workTypes],
      ["assetIds", "Asset", metadata?.assets],
      ["categoryIds", "Category", metadata?.categories],
      [
        "userAssignments",
        "Assigned To",
        assigneeOptions.filter(({ code }) => code.startsWith("user-")),
      ],
      [
        "teamAssignments",
        "Team assignment",
        assigneeOptions.filter(({ code }) => code.startsWith("team-")),
      ],
    ] as const
  ).map(([key, label, options]) => ({
    key,
    label,
    options: lookupOptions(options),
    value: filters[key],
    onChange: (selected: string[]) =>
      updateFilters({ ...filters, [key]: selected }),
  }));
  workOrderFacets.push({
    key: "due-date",
    label: "Due date",
    from: dueDateFrom,
    to: dueDateTo,
    onFromChange: (date) =>
      updateRouteSearch({
        workOrderDueDateFrom: toIsoDate(date),
        workOrderPage: 1,
      }),
    onToChange: (date) =>
      updateRouteSearch({
        workOrderDueDateTo: toIsoDate(date),
        workOrderPage: 1,
      }),
  });
  const orders = data?.workOrders ?? [];
  useEffect(() => {
    if (!data || linkedWorkOrderId) return;
    if (orders.some((order) => order.id === selectedWorkOrderId)) {
      return;
    }
    const nextSelectedWorkOrderId = orders[0]?.id ?? null;
    if (nextSelectedWorkOrderId !== selectedWorkOrderId) {
      if (nextSelectedWorkOrderId) {
        void navigate({
          replace: true,
          to: "/work-orders/$workOrderId",
          params: { workOrderId: nextSelectedWorkOrderId },
          search: (previous) => ({
            ...previous,
            workOrderSelectedId: undefined,
            workOrderFullPage: undefined,
          }),
        });
      } else {
        setRouteState((previous) => ({
          ...previous,
          workOrderSelectedId: undefined,
        }));
        void navigate({
          replace: true,
          to: "/work-orders",
          search: (previous) => ({
            ...previous,
            workOrderSelectedId: undefined,
            workOrderFullPage: undefined,
          }),
        });
      }
    }
  }, [data, linkedWorkOrderId, orders, selectedWorkOrderId, navigate]);
  // Deleting the selected Work Order moves the selection to the next one, or
  // clears it when none remain. A linked route skips the list-based recovery
  // effect above, so the move has to happen here.
  function selectAfterDeletion(deletedWorkOrderId: WorkOrderId) {
    const nextWorkOrderId =
      orders.find((order) => order.id !== deletedWorkOrderId)?.id ?? null;
    if (nextWorkOrderId) {
      void navigate({
        replace: true,
        to: "/work-orders/$workOrderId",
        params: { workOrderId: nextWorkOrderId },
        search: (previous) => ({
          ...previous,
          workOrderSelectedId: undefined,
          workOrderFullPage: undefined,
        }),
      });
      return;
    }
    setRouteState((previous) => ({
      ...previous,
      workOrderSelectedId: undefined,
    }));
    void navigate({
      replace: true,
      to: "/work-orders",
      search: (previous) => ({
        ...previous,
        workOrderSelectedId: undefined,
        workOrderFullPage: undefined,
      }),
    });
  }
  function openFullPage(workOrderId: WorkOrderId) {
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: (previous) => ({
        ...buildWorkOrderDetailSearch(
          previous,
          selectedCustomer && selectedFacility
            ? getWorkScopeSearch(selectedCustomer, selectedFacility)
            : {},
        ),
        workOrderFullPage: true,
      }),
    });
  }
  function openSplitView(workOrderId: WorkOrderId) {
    void navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: (previous) => ({
        ...previous,
        workOrderSelectedId: undefined,
        workOrderFullPage: undefined,
      }),
    });
  }
  return (
    <AppShellContent>
      <WorkPage
        alerts={
          query.isRefetchError && data ? (
            <Alert
              title="Showing Work Orders as of the last successful refresh."
              tone="warning"
              action={
                <Button
                  onClick={() => void query.refetch()}
                  size="sm"
                  variant="outline"
                >
                  Retry
                </Button>
              }
            />
          ) : null
        }
        header={
          <Group justify="between" wrap="wrap">
            <Headline as="h1" size="lg">
              Work Orders
            </Headline>
            {canCreateWorkOrder ? (
              <Button
                icon="plus-regular"
                onClick={() => setIsNewWorkOrderOpen(true)}
                size="sm"
              >
                New work order
              </Button>
            ) : null}
          </Group>
        }
        tabs={
          <Tabs
            value={view}
            onValueChange={(value) => {
              updateRouteSearch({
                workOrderView: value as WorkOrderTab,
                workOrderPage: 1,
              });
            }}
          >
            <TabsList aria-label="Work Order views">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  chipLabel={metadata?.counts[tab.countKey] ?? 0}
                  value={tab.value}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
        toolbar={
          <SearchFilterToolbar
            onSearchChange={(value) => {
              updateRouteSearch({
                workOrderSearch: value,
                workOrderPage: 1,
              });
            }}
            searchAriaLabel="Search work orders"
            searchPlaceholder="Search WO, asset, title..."
            searchValue={search}
          >
            <ListFilters
              activeFilterCount={activeFilterCount}
              ariaLabel="Work Order filters"
              canReset={hasActiveFilters}
              facets={workOrderFacets}
              onReset={clearFilters}
            />
          </SearchFilterToolbar>
        }
      >
        {query.isPending ? (
          <WorkPageState>
            <Text tone="subtle">Loading work orders…</Text>
          </WorkPageState>
        ) : query.isError && !data ? (
          <WorkPageState tone="negative">
            <Text tone="negative">Unable to load work orders.</Text>
            <Button
              onClick={() => void query.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          </WorkPageState>
        ) : (
          <WorkOrderSplitView
            canAssignToMe={canAssignWorkOrdersToMe}
            canDelete={canDeleteWorkOrders}
            canMarkDone={canMarkWorkOrdersDone}
            hasMore={query.hasNextPage ?? false}
            isLoadingMore={query.isFetchingNextPage}
            isLoadMoreError={query.isFetchNextPageError}
            emptyState={
              orders.length === 0 ? (
                <EmptyState
                  actions={
                    hasActiveFilters ? (
                      <Button
                        onClick={clearFilters}
                        size="sm"
                        variant="outline"
                      >
                        Clear Filters
                      </Button>
                    ) : undefined
                  }
                  description={
                    hasActiveFilters
                      ? "Adjust your search or filters and try again."
                      : "Work orders for this facility will appear here."
                  }
                  icon="circle-check-regular"
                  title={
                    hasActiveFilters
                      ? "No work orders match your filters"
                      : "No work orders"
                  }
                />
              ) : undefined
            }
            facilityTimeZone={selectedFacility?.timezone}
            onShowMore={() => {
              if (query.isFetchNextPageError) {
                if (!query.isFetching) void query.fetchNextPage();
              } else {
                updateRouteSearch({ workOrderPage: page + 1 });
              }
            }}
            onDeleted={selectAfterDeletion}
            onOpenFullPage={openFullPage}
            onSelectWorkOrder={openSplitView}
            orders={orders}
            originSearch={routeState}
            prioritySortDirection={prioritySortDirection}
            onTogglePrioritySort={() =>
              setPrioritySortDirection((direction) =>
                direction === "asc" ? "desc" : "asc",
              )
            }
            selectedWorkOrderId={selectedWorkOrderId}
          />
        )}
      </WorkPage>
      <AssetWorkOrderModal
        asset={null}
        onCreate={createWorkOrder}
        onAttachProcedure={attachProcedure}
        onCreated={(workOrderId) => {
          void query.refetch();
          openSplitView(workOrderId);
        }}
        onOpenChange={setIsNewWorkOrderOpen}
        open={isNewWorkOrderOpen}
      />
    </AppShellContent>
  );
}
